// Only used as a base so `new URL()` can parse a relative `deepLink` - never
// actually part of the output. A naive `split('?')` breaks on a deep link
// that already has a fragment (the params would land inside the fragment,
// losing attribution) or a second literal `?` in its query; parsing through
// URL and reassembling pathname+search+hash handles both correctly.
const DUMMY_URL_BASE = 'http://dummy-base.invalid'

/**
 * Appends `source`/`notificationType` query params to a notification's
 * `deepLink`, so click-through attribution survives a full navigation/reload
 * (riding on the destination URL's own query string, rather than a header or
 * a separate attribution call). Shared across every surface that turns a
 * notification into a clickable destination (tray, overlay) - each surface
 * passes its own `source` value so downstream analysis can tell them apart.
 *
 * A malformed `content.deepLink` from the notification-service (bad
 * percent-encoding, an invalid host, etc.) is out of our control - one bad
 * notification shouldn't be able to crash the caller. Returns `undefined` on
 * an unparseable deep link rather than falling back to the raw string: we
 * can't attach attribution to it either way, and a link we couldn't even
 * parse isn't one we can be confident actually navigates anywhere - callers
 * treat this the same as "no destination at all" (a non-interactive row)
 * rather than risk a broken/un-attributed link.
 */
export const appendClickSourceParams = (
  deepLink: string,
  notificationType: string,
  source: string
): string | undefined => {
  let url: URL
  try {
    url = new URL(deepLink, DUMMY_URL_BASE)
  } catch {
    return undefined
  }
  url.searchParams.set('source', source)
  url.searchParams.set('notificationType', notificationType)
  // `deepLink` may be absolute (keep it absolute) or relative (the common
  // case - strip the dummy origin back off so we don't invent one).
  return url.origin === DUMMY_URL_BASE
    ? `${url.pathname}${url.search}${url.hash}`
    : url.href
}
